import { useEffect, useState } from 'react';
import { FieldConfig } from '../../../../../components/CustomFormsComponents/CustomField';
import { useAppSelector } from '../../../../../hooks/reduxHooks';
import { TEST_IDS } from '../testIds';

const BOOLEAN_OPTIONS = [
    { id: 1, value: true, nombre: 'Si' },
    { id: 0, value: false, nombre: 'No' }
];

const useRadiologicalEquipmentFieldsConfig = ({ t, formData, handleBlurValidation }: any) => {
    const [radiologicalEquipmentFieldsConfig, setRadiologicalEquipmentFieldsConfig] = useState<
        FieldConfig[]
    >([]);

    const carcinogenicParametricData = useAppSelector(
        (state) => state.carcinogenicReport.carcinogenicParametricData
    );

    const carcinogenicReportData = useAppSelector(
        (state) => state.carcinogenicReport.carcinogenicReport
    );

    useEffect(() => {
        const fieldsConfig: FieldConfig[] = [
            {
                type: 'text',
                label: t('radiologicalEquipmentForm.inputs.equipment.brand') as string,
                name: 'brand',
                required: true,
                value: formData.brand,
                disabled: false,
                helperText: t('radiologicalEquipment.inputs.brand') as string,
                onBlurValidation: handleBlurValidation,
                gridSpace: { xs: 12, sm: 6 },
                testID: TEST_IDS.newRadiologicalEquipment.radiologicalEquipmentFields.brand,
                validations: [{ name: 'required' }, { name: 'maxLength', param: 100 }]
            },
            {
                type: 'text',
                label: t('radiologicalEquipmentForm.inputs.equipment.model') as string,
                name: 'model',
                required: true,
                value: formData.model,
                disabled: false,
                helperText: t('radiologicalEquipment.inputs.model') as string,
                onBlurValidation: handleBlurValidation,
                gridSpace: { xs: 12, sm: 6 },
                testID: TEST_IDS.newRadiologicalEquipment.radiologicalEquipmentFields.model,
                validations: [{ name: 'required' }, { name: 'maxLength', param: 100 }]
            },
            {
                type: 'autocomplete',
                label: t('radiologicalEquipmentForm.inputs.equipment.equipmentType') as string,
                name: 'equipmentType',
                required: true,
                value: formData.equipmentType,
                disabled: false,
                helperText: t('radiologicalEquipment.inputs.equipmentType') as string,
                onBlurValidation: handleBlurValidation,
                gridSpace: { xs: 12, sm: 6 },
                options: carcinogenicParametricData?.tiposEquipo || [],
                getOptionLabel: (option) => option?.descripcion || '',
                testID: TEST_IDS.newRadiologicalEquipment.radiologicalEquipmentFields.equipmentType,
                validations: [{ name: 'required' }]
            },
            {
                type: 'autocomplete',
                label: t('radiologicalEquipmentForm.inputs.equipment.substance') as string,
                name: 'substance',
                required: true,
                value: formData.substance,
                disabled: false,
                helperText: t('radiologicalEquipment.inputs.substance') as string,
                onBlurValidation: handleBlurValidation,
                gridSpace: { xs: 12, sm: 6 },
                options: carcinogenicReportData?.sustancias || [],
                getOptionLabel: (option) => option?.descripcion || '',
                testID: TEST_IDS.newRadiologicalEquipment.radiologicalEquipmentFields.substance,
                validations: [{ name: 'required' }]
            },
            {
                type: 'autocomplete',
                label: t('radiologicalEquipmentForm.inputs.equipment.characteristics') as string,
                name: 'characteristics',
                required: true,
                value: formData.characteristics,
                disabled: false,
                helperText: t('radiologicalEquipment.inputs.characteristics') as string,
                onBlurValidation: handleBlurValidation,
                gridSpace: { xs: 12, sm: 6 },
                options: carcinogenicParametricData?.caracteristicasEquipo || [],
                getOptionLabel: (option) => option?.descripcion || '',
                testID: TEST_IDS.newRadiologicalEquipment.radiologicalEquipmentFields
                    .characteristics,
                validations: [{ name: 'required' }]
            },
            {
                type: 'autocomplete',
                label: t('radiologicalEquipmentForm.inputs.equipment.radiationType') as string,
                name: 'radiationType',
                required: true,
                value: formData.radiationType,
                disabled: false,
                helperText: t('radiologicalEquipment.inputs.radiationType') as string,
                onBlurValidation: handleBlurValidation,
                gridSpace: { xs: 12, sm: 6 },
                options: carcinogenicParametricData?.tiposRadiacion || [],
                getOptionLabel: (option) => option?.descripcion || '',
                testID: TEST_IDS.newRadiologicalEquipment.radiologicalEquipmentFields.radiationType,
                validations: [{ name: 'required' }]
            },
            {
                type: 'text',
                label: t('radiologicalEquipmentForm.inputs.equipment.activityCURIE') as string,
                name: 'activityCURIE',
                required: true,
                value: formData.activityCURIE,
                disabled: false,
                helperText: t('radiologicalEquipment.inputs.activityCURIE') as string,
                onBlurValidation: handleBlurValidation,
                gridSpace: { xs: 12, sm: 4 },
                testID: TEST_IDS.newRadiologicalEquipment.radiologicalEquipmentFields.activityCURIE,
                validations: [{ name: 'required' }, { name: 'maxLength', param: 50 }]
            },
            {
                type: 'text',
                label: t(
                    'radiologicalEquipmentForm.inputs.equipment.radioisotopeOfTheSource'
                ) as string,
                name: 'radioisotopeOfTheSource',
                required: false,
                value: formData.radioisotopeOfTheSource,
                disabled: false,
                helperText: t('radiologicalEquipment.inputs.radioisotopeOfTheSource') as string,
                onBlurValidation: handleBlurValidation,
                gridSpace: { xs: 12, sm: 4 },
                testID: TEST_IDS.newRadiologicalEquipment.radiologicalEquipmentFields
                    .radioisotopeOfTheSource,
                validations: [{ name: 'maxLength', param: 50 }]
            },
            {
                type: 'autocomplete',
                label: t('radiologicalEquipmentForm.inputs.equipment.sourceType') as string,
                name: 'sourceType',
                required: true,
                value: formData.sourceType,
                disabled: false,
                helperText: t('radiologicalEquipment.inputs.sourceType') as string,
                onBlurValidation: handleBlurValidation,
                gridSpace: { xs: 12, sm: 4 },
                options: carcinogenicParametricData?.tiposFuente || [],
                getOptionLabel: (option) => option?.descripcion || '',
                testID: TEST_IDS.newRadiologicalEquipment.radiologicalEquipmentFields.sourceType,
                validations: [{ name: 'required' }]
            },
            {
                type: 'text',
                label: t('radiologicalEquipmentForm.inputs.equipment.equipmentPowerKVP') as string,
                name: 'equipmentPowerKVP',
                required: true,
                value: formData.equipmentPowerKVP,
                disabled: false,
                inputType: 'number',
                helperText: t('radiologicalEquipment.inputs.equipmentPowerKVP') as string,
                onBlurValidation: handleBlurValidation,
                gridSpace: { xs: 12, sm: 4 },
                testID: TEST_IDS.newRadiologicalEquipment.radiologicalEquipmentFields
                    .equipmentPowerKVP,
                validations: [
                    { name: 'required' },
                    { name: 'maxLength', param: 20 },
                    { name: 'minValue', param: 0 }
                ]
            },
            {
                type: 'text',
                label: t('radiologicalEquipmentForm.inputs.equipment.equipmentPowerMAP') as string,
                name: 'equipmentPowerMAP',
                required: true,
                value: formData.equipmentPowerMAP,
                disabled: false,
                inputType: 'number',
                helperText: t('radiologicalEquipment.inputs.equipmentPowerMAP') as string,
                onBlurValidation: handleBlurValidation,
                gridSpace: { xs: 12, sm: 4 },
                testID: TEST_IDS.newRadiologicalEquipment.radiologicalEquipmentFields
                    .equipmentPowerMAP,
                validations: [
                    { name: 'required' },
                    { name: 'maxLength', param: 20 },
                    { name: 'minValue', param: 0 }
                ]
            },
            {
                type: 'text',
                label: t('radiologicalEquipmentForm.inputs.equipment.equipmentPowerMEV') as string,
                name: 'equipmentPowerMEV',
                required: true,
                value: formData.equipmentPowerMEV,
                disabled: false,
                inputType: 'number',
                helperText: t('radiologicalEquipment.inputs.equipmentPowerMEV') as string,
                onBlurValidation: handleBlurValidation,
                gridSpace: { xs: 12, sm: 4 },
                testID: TEST_IDS.newRadiologicalEquipment.radiologicalEquipmentFields
                    .equipmentPowerMEV,
                validations: [
                    { name: 'required' },
                    { name: 'maxLength', param: 20 },
                    { name: 'minValue', param: 0 }
                ]
            },
            {
                type: 'autocomplete',
                label: t('radiologicalEquipmentForm.inputs.equipment.imageProcessing') as string,
                name: 'imageProcessing',
                required: true,
                value: formData.imageProcessing,
                disabled: false,
                helperText: t('radiologicalEquipment.inputs.imageProcessing') as string,
                onBlurValidation: handleBlurValidation,
                gridSpace: { xs: 12, sm: 6 },
                options: BOOLEAN_OPTIONS,
                getOptionLabel: (option) => option?.nombre || '',
                testID: TEST_IDS.newRadiologicalEquipment.radiologicalEquipmentFields
                    .imageProcessing,
                validations: [{ name: 'required' }]
            },
            {
                type: 'autocomplete',
                label: t('radiologicalEquipmentForm.inputs.equipment.processingMethod') as string,
                name: 'processingMethod',
                required: true,
                value: formData.processingMethod,
                disabled: false,
                helperText: t('radiologicalEquipment.inputs.processingMethod') as string,
                onBlurValidation: handleBlurValidation,
                gridSpace: { xs: 12, sm: 6 },
                options: carcinogenicParametricData?.metodosProcesadoImagen || [],
                getOptionLabel: (option) => option?.descripcion || '',
                testID: TEST_IDS.newRadiologicalEquipment.radiologicalEquipmentFields
                    .processingMethod,
                validations: [{ name: 'required' }]
            },
            {
                type: 'textarea',
                label: t('radiologicalEquipmentForm.inputs.equipment.observations') as string,
                name: 'observations',
                required: false,
                value: formData.observations,
                disabled: false,
                helperText: t('radiologicalEquipment.inputs.observations') as string,
                onBlurValidation: handleBlurValidation,
                gridSpace: { xs: 12 },
                testID: TEST_IDS.newRadiologicalEquipment.radiologicalEquipmentFields.observations,
                validations: [{ name: 'maxLength', param: 500 }]
            }
        ];

        setRadiologicalEquipmentFieldsConfig(fieldsConfig);
    }, [t]);

    return { radiologicalEquipmentFieldsConfig };
};

export default useRadiologicalEquipmentFieldsConfig;
